/**
 * ログ出力ユーティリティ
 * ログレベル制御と構造化ログ出力を提供します
 */

import { getPublicRuntimeConfig } from '../config/env';

/** ログレベル定義 */
export const LOG_LEVELS = Object.freeze({
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
});

/** ログレベル優先順位 */
const LOG_PRIORITIES = Object.freeze({
  [LOG_LEVELS.DEBUG]: 10,
  [LOG_LEVELS.INFO]: 20,
  [LOG_LEVELS.WARN]: 30,
  [LOG_LEVELS.ERROR]: 40,
});

/**
 * 出力対象かどうかを判定する
 * @param {'debug' | 'info' | 'warn' | 'error'} targetLevel - 出力対象レベル
 * @param {'debug' | 'info' | 'warn' | 'error'} currentLevel - 現在設定レベル
 * @returns {boolean} 出力対象ならtrue
 */
const shouldOutput = (targetLevel, currentLevel) => {
  /** 出力対象レベルの優先順位 */
  const targetPriority = LOG_PRIORITIES[targetLevel] ?? LOG_PRIORITIES[LOG_LEVELS.INFO];

  /** 現在設定レベルの優先順位 */
  const currentPriority = LOG_PRIORITIES[currentLevel] ?? LOG_PRIORITIES[LOG_LEVELS.INFO];

  return targetPriority >= currentPriority;
};

/**
 * 構造化ログオブジェクトを作成する
 * @param {'debug' | 'info' | 'warn' | 'error'} level - ログレベル
 * @param {string} message - ログメッセージ
 * @param {Object} [metadata={}] - 追加情報
 * @returns {{ level: string, message: string, metadata: Object, timestamp: string }} ログオブジェクト
 */
const buildLogPayload = (level, message, metadata = {}) => {
  /** ログ出力時刻 */
  const timestamp = new Date().toISOString();

  return {
    level,
    message,
    metadata,
    timestamp,
  };
};

/**
 * ログを出力する
 * @param {'debug' | 'info' | 'warn' | 'error'} level - ログレベル
 * @param {string} message - ログメッセージ
 * @param {Object} [metadata={}] - 追加情報
 * @returns {void}
 */
export const log = (level, message, metadata = {}) => {
  /** ランタイム設定 */
  const runtimeConfig = getPublicRuntimeConfig();

  /** 現在のログレベル */
  const currentLogLevel = runtimeConfig.logLevel;

  if (!shouldOutput(level, currentLogLevel)) {
    return;
  }

  /** 出力対象のログオブジェクト */
  const payload = buildLogPayload(level, message, metadata);

  if (level === LOG_LEVELS.ERROR) {
    console.error(payload);
    return;
  }

  if (level === LOG_LEVELS.WARN) {
    console.warn(payload);
    return;
  }

  console.log(payload);
};

/**
 * INFOログを出力する
 * @param {string} message - ログメッセージ
 * @param {Object} [metadata={}] - 追加情報
 * @returns {void}
 */
export const logInfo = (message, metadata = {}) => {
  log(LOG_LEVELS.INFO, message, metadata);
};

/**
 * WARNログを出力する
 * @param {string} message - ログメッセージ
 * @param {Object} [metadata={}] - 追加情報
 * @returns {void}
 */
export const logWarn = (message, metadata = {}) => {
  log(LOG_LEVELS.WARN, message, metadata);
};

/**
 * ERRORログを出力する
 * @param {string} message - ログメッセージ
 * @param {Object} [metadata={}] - 追加情報
 * @returns {void}
 */
export const logError = (message, metadata = {}) => {
  log(LOG_LEVELS.ERROR, message, metadata);
};
